import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, post, fmt, ApiError } from '../../lib/api';

/**
 * THE REFUND FRAUD CONTROL (POS_MASTER_PLAN.md §4.6 Problem 3).
 *
 * With an unintegrated terminal, a cashier can refund money to their own card
 * and our software would never know. Two things close that gap: every terminal
 * refund must be authorised by the POS first, and every one must come back with
 * an RRN. Anything still on this list has NOT been confirmed against the
 * terminal — a manager must clear it daily.
 */
export default function Exceptions() {
  const qc = useQueryClient();
  const [rrn, setRrn] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const list = useQuery({
    queryKey: ['exceptions'],
    queryFn: () => get<any>('/api/v1/returns/exceptions'),
  });

  const settle = useMutation({
    mutationFn: (v: { id: string; rrn: string }) => post(`/api/v1/returns/${v.id}/settle`, { rrn: v.rrn }),
    onSuccess: () => { setError(''); void qc.invalidateQueries({ queryKey: ['exceptions'] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not settle'),
  });

  const d = list.data;

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold">Refund exceptions</h1>
        <p className="text-sm text-gray-400 mt-1">
          Card refunds with no confirmed terminal RRN. Clear this list every day.
        </p>
      </div>

      <div className={`card p-5 border-l-4 ${
        (d?.count ?? 0) === 0 ? 'border-l-emerald-500' : 'border-l-red-500'
      }`}>
        <div className="flex items-baseline justify-between">
          <div>
            <div className="font-medium">Pending settlement</div>
            <div className="text-xs text-gray-500 mt-0.5">{d?.note}</div>
          </div>
          <span className={`money text-3xl font-bold ${
            (d?.count ?? 0) === 0 ? 'text-emerald-400' : 'text-red-400'
          }`}>{d?.count ?? 0}</span>
        </div>
      </div>

      {error && <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-2">{error}</div>}

      {d?.items?.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr>
              <th className="th">Credit note</th><th className="th">Store</th>
              <th className="th">Cashier</th><th className="th">Reason</th>
              <th className="th">Amount</th><th className="th">Raised</th><th className="th">Terminal RRN</th>
            </tr></thead>
            <tbody>
              {d.items.map((r: any) => (
                <tr key={r.id}>
                  <td className="td font-mono text-xs">{r.creditNoteNo}</td>
                  <td className="td">{r.storeCode}</td>
                  <td className="td">{r.cashier}</td>
                  <td className="td">
                    {r.reasonCode.replace('_', ' ')}
                    {r.isBlind && <span className="badge bg-red-900/50 text-red-300 ml-2">blind</span>}
                  </td>
                  <td className="td money">{fmt(r.totalMinor)}</td>
                  <td className="td text-xs text-gray-500">{new Date(r.createdAt).toLocaleString('en-GB')}</td>
                  <td className="td">
                    <div className="flex gap-1">
                      <input className="input py-1 w-32 text-xs" placeholder="RRN"
                        value={rrn[r.id] ?? ''}
                        onChange={(e) => setRrn((p) => ({ ...p, [r.id]: e.target.value }))} />
                      <button className="btn-primary text-xs py-1 px-2"
                        disabled={!(rrn[r.id] ?? '').trim() || settle.isPending}
                        onClick={() => settle.mutate({ id: r.id, rrn: rrn[r.id].trim() })}>
                        Settle
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card p-5">
        <div className="label">Why this page exists</div>
        <div className="text-sm text-gray-300 space-y-2">
          <p>
            CrediMax terminals that are not integrated with the POS do not tell our software
            anything. So a refund performed on the terminal is invisible to us — which means
            a cashier could refund money to their own card with no sale behind it.
          </p>
          <p>
            The control is twofold: the POS prints a Refund Authorisation slip with a one-time
            code and the exact amount, and the credit note stays on this list until the
            terminal's RRN is entered.
          </p>
          <p className="text-amber-300">
            The missing half is a daily settlement file from CrediMax. Reconciling their
            settlement data against these records is what catches a terminal refund that has
            no matching credit note. Request it — it is item 7 in the CrediMax letter.
          </p>
          <p className="text-gray-400 text-xs">
            Once ECR integration is live this page should stay empty permanently, because the
            POS will drive the refund itself and receive the RRN programmatically.
          </p>
        </div>
      </div>
    </div>
  );
}
